/**
 * One asset: what it is, where it sits in the hierarchy, its health and why,
 * its life and value, the work done on it, its plans, and what it measures.
 *
 * The edit form writes the register and lifecycle fields through the audited
 * PATCH; a figure that came from the asset-type policy is labelled 'policy'
 * wherever it is shown, so nobody mistakes a planning assumption for the
 * asset's own record.
 */
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useAmAddNote, useAmAsset, useAmCreatePlan, useAmHistory, useAmNotes } from "../../api/queries";
import { AssetDialog } from "./AssetDialog";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Select,
  Skeleton,
  Table,
} from "../../design/components";
import {
  BAND_LABEL,
  fieldLabel,
  METHOD_LABEL,
  NOTE_CATEGORY_LABEL,
  PLAN_STATE_LABEL,
  type AmAssetDetail,
  type AmHistoryKind,
  type NoteCategory,
} from "../../domain/assetManagement";
import { formatDateTime } from "../../lib/format";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import {
  Criticality,
  day,
  DEFECT_STATUS_LABEL,
  errorText,
  HealthChip,
  KIND_LABEL,
  money,
  PolicyTag,
  s,
  StageLabel,
  SUITE,
  Tiles,
  TrendLine,
} from "./shared";
import { STATUS_LABEL } from "../../domain/workOrders";
import type { WorkOrderStatus } from "../../domain/workOrders";

type Tab = "overview" | "history" | "notes" | "work" | "plans" | "data";
const TABS: { value: Tab; label: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "history", label: "History" },
  { value: "notes", label: "Notes" },
  { value: "work", label: "Work and defects" },
  { value: "plans", label: "Maintenance plans" },
  { value: "data", label: "Readings and documents" },
];

export function AssetDetailPage() {
  const { id } = useParams();
  const session = useSession();
  const q = useAmAsset(id);
  const [tab, setTab] = useState<Tab>("overview");
  const [editing, setEditing] = useState(false);
  const d = q.data;
  const canEdit = hasPermission(session.principal, PERMISSION.assetWrite);
  const canNote = hasPermission(session.principal, PERMISSION.notesWrite);

  useScreenContext(
    d
      ? {
          route: "/asset-register",
          title: d.asset.tag,
          summary: [
            t("{tag} — {name}, {type}, criticality {c} of 5.", {
              tag: d.asset.tag,
              name: d.asset.name,
              type: d.asset.assetTypeName,
              c: d.asset.criticality,
            }),
            d.asset.healthBand != null
              ? t("Health {score} ({band}).", {
                  score: d.asset.healthScore ?? "",
                  band: t(BAND_LABEL[d.asset.healthBand]),
                })
              : t("Health not assessed."),
          ],
          offers: [{ label: t("Why is its health this?"), ask: `why is the health of ${d.asset.tag} what it is?` }],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title={d ? `${d.asset.tag} · ${d.asset.name}` : t("Asset")}
      actions={
        <div className={s.actions}>
          {d && canEdit && (
            <Button variant="primary" onClick={() => setEditing(true)} data-testid="am-edit">
              {t("Edit")}
            </Button>
          )}
          <Link to="/asset-register">{t("Back to the register")}</Link>
        </div>
      }
    >
      {editing && d && <AssetDialog assetId={d.asset.id} onClose={() => setEditing(false)} />}
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <ErrorState error={q.error} retry={() => void q.refetch()} />}
      {d && (
        <>
          <div className={s.crumbs}>
            {[t("Site"), ...d.zonePath.map((z) => z.name)].join(" › ")}
            {d.asset.parentTag && (
              <>
                {" › "}
                <Link to={`/asset-register/${encodeURIComponent(d.asset.parentId ?? "")}`}>{d.asset.parentTag}</Link>
              </>
            )}
          </div>
          <Tiles
            items={[
              {
                key: "health",
                label: t("Health"),
                value: d.asset.healthScore ?? "—",
                sub: d.asset.healthBand != null ? t(BAND_LABEL[d.asset.healthBand]) : t("Not assessed"),
                tone:
                  d.asset.healthBand == null
                    ? undefined
                    : d.asset.healthBand <= 2
                      ? "bad"
                      : d.asset.healthBand === 3
                        ? "warn"
                        : "good",
              },
              {
                key: "life",
                label: t("Life remaining"),
                value: d.life.remainingYears ?? "—",
                unit: d.life.remainingYears != null ? t("years") : undefined,
                sub:
                  d.life.consumedPct != null
                    ? t("{p}% of a {life}-year life used", {
                        p: d.life.consumedPct,
                        life: d.life.designLifeYears ?? "",
                      })
                    : t("Needs an install date and a design life"),
                tone: d.life.remainingYears != null && d.life.remainingYears < 0 ? "bad" : undefined,
              },
              {
                key: "failures",
                label: t("Failures, 24 months"),
                value: d.reliability.failures,
                sub:
                  d.reliability.mtbfDays != null
                    ? t("MTBF {mtbf} days · MTTR {mttr} h", {
                        mtbf: d.reliability.mtbfDays,
                        mttr: d.reliability.mttrHours ?? "—",
                      })
                    : t("No corrective work recorded"),
              },
              { key: "cost", label: t("Maintenance cost to date"), value: money(d.costToDate) },
            ]}
          />
          <Segmented value={tab} options={TABS} onChange={setTab} ariaLabel="Asset sections" />
          {tab === "overview" && <Overview d={d} />}
          {tab === "history" && <History assetId={d.asset.id} />}
          {tab === "notes" && <Notes assetId={d.asset.id} canWrite={canNote} />}
          {tab === "work" && <Work d={d} />}
          {tab === "plans" && <Plans d={d} canEdit={canEdit} />}
          {tab === "data" && <Data d={d} />}
        </>
      )}
    </Page>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children ?? "—"}</dd>
    </>
  );
}

function Overview({ d }: { d: AmAssetDetail }) {
  const a = d.asset;
  const basis = a.healthBasis ?? {};
  return (
    <div className={s.split}>
      <Card title={t("Register and lifecycle")}>
        {
          <dl className={s.kv}>
            <Row label={t("Type")}>{a.assetTypeName}</Row>
            <Row label={t("Zone")}>{a.zoneName ? `${a.zoneName} (${a.zoneCode})` : null}</Row>
            <Row label={t("Criticality")}>
              <Criticality value={a.criticality} />
            </Row>
            <Row label={t("Stage")}>
              <StageLabel stage={a.stage} />
            </Row>
            <Row label={t("Manufacturer")}>{a.manufacturer}</Row>
            <Row label={t("Model")}>{a.model}</Row>
            <Row label={t("Serial number")}>{a.serialNo}</Row>
            <Row label={t("Installed")}>{a.installedOn}</Row>
            <Row label={t("Purchased")}>{a.purchasedOn}</Row>
            <Row label={t("Purchase cost")}>{a.purchaseCost != null ? money(a.purchaseCost) : null}</Row>
            <Row label={t("Replacement value")}>
              {d.life.replacementValue != null ? (
                <>
                  {money(d.life.replacementValue)}
                  <PolicyTag source={d.life.valueSource} />
                </>
              ) : null}
            </Row>
            <Row label={t("Design life")}>
              {d.life.designLifeYears != null ? (
                <>
                  {t("{n} years", { n: d.life.designLifeYears })}
                  <PolicyTag source={d.life.lifeSource} />
                </>
              ) : null}
            </Row>
            <Row label={t("End of life")}>{d.life.endOfLife}</Row>
            <Row label={t("Book value")}>
              {d.life.bookValue != null
                ? `${money(d.life.bookValue)} · ${t(METHOD_LABEL[a.depreciationMethod])}`
                : null}
            </Row>
            <Row label={t("Warranty until")}>
              {a.warrantyUntil
                ? `${a.warrantyUntil}${d.life.warrantyDaysLeft != null && d.life.warrantyDaysLeft < 0 ? ` · ${t("expired")}` : ""}`
                : null}
            </Row>
          </dl>
        }
        {Object.keys(a.attrs ?? {}).length > 0 && (
          <>
            <h3 className={s.subhead}>{t("{type} details", { type: a.assetTypeName })}</h3>
            <dl className={s.kv}>
              {Object.entries(a.attrs).map(([k, v]) => (
                <Row key={k} label={fieldLabel(k)}>
                  {typeof v === "string" ? fieldLabel(v) : v == null ? null : String(v)}
                </Row>
              ))}
            </dl>
          </>
        )}
      </Card>
      <Card
        title={t("Health and why")}
        sub={t("Condition minus open defects, age and predicted failure.")}
      >
        {a.healthBand == null ? (
          <EmptyState>
            {t("Not assessed: no condition, defect, age or forecast recorded.")}
          </EmptyState>
        ) : (
          <>
            <p>
              <HealthChip band={a.healthBand} score={a.healthScore} />
            </p>
            <dl className={s.kv}>
              {"condition" in basis && <Row label={t("Surveyed condition")}>{String(basis.condition)}</Row>}
              {"open_defects" in basis && (
                <Row label={t("Open defects")}>
                  {t("{n} open, worst severity {w}, −{p}", {
                    n: (basis.open_defects as { count: number }).count,
                    w: (basis.open_defects as { worst: number }).worst,
                    p: (basis.open_defects as { penalty: number }).penalty,
                  })}
                </Row>
              )}
              {"life_consumed_pct" in basis && (
                <Row label={t("Age")}>
                  {t("{p}% of design life, −{a}", { p: String(basis.life_consumed_pct), a: String(basis.age_penalty) })}
                </Row>
              )}
              {"failure_probability" in basis && (
                <Row label={t("Predicted failure")}>
                  {t("{p}% probability, −{a}", {
                    p: Math.round(Number(basis.failure_probability) * 100),
                    a: String(basis.prediction_penalty),
                  })}
                </Row>
              )}
            </dl>
            {d.healthHistory.length > 1 && (
              <TrendLine
                label={t("Health over time")}
                points={d.healthHistory.map((h) => ({ label: h.observedOn.slice(5), value: h.score }))}
              />
            )}
          </>
        )}
      </Card>
      {d.children.length > 0 && (
        <Card title={t("Components")} sub={t("Assets fitted inside this one.")}>
          <Table>
            <thead>
              <tr>
                <th>{t("Tag")}</th>
                <th>{t("Name")}</th>
                <th>{t("Health")}</th>
                <th>{t("Stage")}</th>
              </tr>
            </thead>
            <tbody>
              {d.children.map((c) => (
                <tr key={c.id}>
                  <td className={s.mono}>
                    <Link to={`/asset-register/${encodeURIComponent(c.id)}`}>{c.tag}</Link>
                  </td>
                  <td>{c.name}</td>
                  <td>
                    <HealthChip band={c.healthBand} />
                  </td>
                  <td>
                    <StageLabel stage={c.stage} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function Work({ d }: { d: AmAssetDetail }) {
  return (
    <div className={s.split}>
      <Card title={t("Work orders")} sub={t("Work raised against this asset, newest first.")}>
        {d.workOrders.length ? (
          <Table>
            <thead>
              <tr>
                <th>{t("Number")}</th>
                <th>{t("Kind")}</th>
                <th>{t("Title")}</th>
                <th>{t("Status")}</th>
                <th>{t("Opened")}</th>
                <th className={s.num}>{t("Cost")}</th>
              </tr>
            </thead>
            <tbody>
              {d.workOrders.map((w) => (
                <tr key={w.id}>
                  <td className={s.mono}>
                    <Link to={`/work-orders/${encodeURIComponent(w.id)}`}>{w.number}</Link>
                  </td>
                  <td>{t(KIND_LABEL[w.kind] ?? w.kind)}</td>
                  <td>{w.title}</td>
                  <td>{t(STATUS_LABEL[w.status as WorkOrderStatus] ?? w.status)}</td>
                  <td className={s.mono}>{day(w.openedAt)}</td>
                  <td className={s.num}>{w.costActual != null ? money(w.costActual) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>{t("No work order is recorded against this asset.")}</EmptyState>
        )}
      </Card>
      <Card title={t("Defects")}>
        {d.defects.length ? (
          <Table>
            <thead>
              <tr>
                <th>{t("Severity")}</th>
                <th>{t("Finding")}</th>
                <th>{t("Status")}</th>
                <th>{t("Found")}</th>
              </tr>
            </thead>
            <tbody>
              {d.defects.map((x) => (
                <tr key={x.id}>
                  <td className={s.mono}>{x.severity}</td>
                  <td>{x.summary ?? x.defectType ?? "—"}</td>
                  <td>{t(DEFECT_STATUS_LABEL[x.status] ?? x.status)}</td>
                  <td className={s.mono}>{day(x.detectedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>{t("No defect is recorded against this asset.")}</EmptyState>
        )}
      </Card>
    </div>
  );
}

function Plans({ d, canEdit }: { d: AmAssetDetail; canEdit: boolean }) {
  const create = useAmCreatePlan();
  const [v, setV] = useState({ title: "", interval: "90", first: "", tasks: "" });
  return (
    <div className={s.split}>
      <Card title={t("Maintenance plans")} sub={t("Preventive work raised automatically when due.")}>
        {d.plans.length ? (
          <Table>
            <thead>
              <tr>
                <th>{t("Plan")}</th>
                <th>{t("Every")}</th>
                <th>{t("Next due")}</th>
                <th>{t("State")}</th>
              </tr>
            </thead>
            <tbody>
              {d.plans.map((p) => (
                <tr key={p.id}>
                  <td>{p.title}</td>
                  <td>
                    {p.trigger === "calendar"
                      ? t("{n} days", { n: p.intervalDays ?? "" })
                      : `${p.intervalUsage ?? ""} ${p.usageMeasurement ?? ""}`}
                  </td>
                  <td className={s.mono}>{day(p.nextDueAt)}</td>
                  <td>{t(PLAN_STATE_LABEL[p.state])}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>{t("No maintenance plan covers this asset.")}</EmptyState>
        )}
      </Card>
      {canEdit && (
        <Card title={t("Add a plan")} sub={t("A calendar plan on this asset.")}>
          <div className={s.form}>
            <Field label="Title">
              <Input
                value={v.title}
                onChange={(e) => setV({ ...v, title: e.target.value })}
                data-testid="am-plan-title"
              />
            </Field>
            <Field label="Every (days)">
              <Input
                inputMode="numeric"
                value={v.interval}
                onChange={(e) => setV({ ...v, interval: e.target.value })}
              />
            </Field>
            <Field label="First due">
              <Input type="date" value={v.first} onChange={(e) => setV({ ...v, first: e.target.value })} />
            </Field>
            <label className={s.formWide}>
              <span className={s.dim}>{t("Tasks, one per line")}</span>
              <textarea
                className={s.textarea}
                style={{ minHeight: 70 }}
                value={v.tasks}
                onChange={(e) => setV({ ...v, tasks: e.target.value })}
              />
            </label>
          </div>
          <div className={s.formFoot}>
            <Button
              variant="primary"
              disabled={v.title.trim().length < 3 || !Number(v.interval) || create.isPending}
              onClick={() =>
                create.mutate(
                  {
                    assetId: d.asset.id,
                    title: v.title.trim(),
                    trigger: "calendar",
                    intervalDays: Number(v.interval),
                    firstDueOn: v.first || undefined,
                    tasks: v.tasks
                      .split("\n")
                      .map((x) => x.trim())
                      .filter(Boolean),
                  },
                  { onSuccess: () => setV({ title: "", interval: "90", first: "", tasks: "" }) },
                )
              }
              data-testid="am-plan-add"
            >
              {t("Add the plan")}
            </Button>
            {create.error && <span className={s.err}>{errorText(create.error)}</span>}
          </div>
        </Card>
      )}
    </div>
  );
}

function Data({ d }: { d: AmAssetDetail }) {
  return (
    <div className={s.split}>
      <Card title={t("Latest readings")}>
        {d.readings.length ? (
          <Table>
            <thead>
              <tr>
                <th>{t("Measurement")}</th>
                <th className={s.num}>{t("Value")}</th>
                <th>{t("When")}</th>
              </tr>
            </thead>
            <tbody>
              {d.readings.map((r, i) => (
                <tr key={`${r.measurement}-${i}`}>
                  <td>{r.measurement}</td>
                  <td className={s.num}>
                    {r.value ?? "—"} {r.unit ?? ""}
                  </td>
                  <td className={s.mono}>{day(r.observedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>{t("No reading is recorded for this asset.")}</EmptyState>
        )}
      </Card>
      <Card title={t("Forecasts")}>
        {d.predictions.length ? (
          <dl className={s.kv}>
            {d.predictions.map((p) => (
              <Row key={p.predictedAt + p.modelKey} label={p.modelKey}>
                {p.probability != null && t("{p}% failure probability", { p: Math.round(p.probability * 100) })}
                {p.rulP50Days != null && ` · ${t("about {n} days of life left", { n: p.rulP50Days })}`}
              </Row>
            ))}
          </dl>
        ) : (
          <EmptyState>{t("No model has scored this asset.")}</EmptyState>
        )}
      </Card>
      <Card title={t("Documents")}>
        {d.documents.length ? (
          <ul>
            {d.documents.map((x) => (
              <li key={x.id}>
                <Link to={`/library`}>{x.title}</Link> {x.relation && <span className={s.dim}>({x.relation})</span>}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>{t("No document is linked to this asset.")}</EmptyState>
        )}
      </Card>
    </div>
  );
}

const EVENT_LABEL: Record<AmHistoryKind, string> = {
  created: "Registered",
  changed: "Changed",
  work_order: "Work order raised",
  work_done: "Work completed",
  defect: "Defect found",
  defect_resolved: "Defect resolved",
  note: "Note",
};

function shown(v: unknown, field = ""): string {
  if (v === null || v === undefined || v === "") return "\u2014";
  if (typeof v === "string") {
    /* a coded value — runway_edge, in_service — reads as words; a tag or a date does not */
    if (/^[a-z][a-z0-9]*(_[a-z0-9]+)+$|^[a-z]+$/.test(v) && field !== "tag") return t(fieldLabel(v));
    return v.length > 60 ? `${v.slice(0, 60)}…` : v;
  }
  return String(v);
}

/* Defaults nobody typed are not worth a line in the history of a new asset. */
const QUIET_DEFAULTS: Record<string, unknown> = { depreciation_method: "straight_line", residual_pct: 0 };

function History({ assetId }: { assetId: string }) {
  const q = useAmHistory(assetId);
  if (q.isLoading) return <Skeleton rows={6} />;
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  const events = q.data ?? [];
  return (
    <Card
      title={t("History")}
      sub={t("Changes, work, defects and notes, newest first.")}
    >
      {events.length ? (
        <ol className={s.timeline} data-testid="am-history">
          {events.map((e, i) => (
            <li key={`${e.at}-${i}`} className={s.event} data-kind={e.kind}>
              <div className={s.eventHead}>
                <b>{t(EVENT_LABEL[e.kind])}</b>
                <span className={s.eventWhen}>{formatDateTime(e.at)}</span>
                {e.who && <span className={s.dim}>{t("by {who}", { who: e.who })}</span>}
              </div>
              {e.kind === "changed" || e.kind === "created" ? (
                e.changes && e.changes.length > 0 ? (
                  <ul className={s.changes}>
                    {e.kind === "created"
                      ? e.changes
                          .filter((c) => QUIET_DEFAULTS[c.field] !== c.after)
                          .map((c) => (
                            <li key={c.field}>
                              {t(fieldLabel(c.field))}: <b>{shown(c.after, c.field)}</b>
                            </li>
                          ))
                      : e.changes.map((c) => (
                          <li key={c.field}>
                            {t(fieldLabel(c.field))}: <del>{shown(c.before, c.field)}</del> →{" "}
                            <b>{shown(c.after, c.field)}</b>
                          </li>
                        ))}
                  </ul>
                ) : null
              ) : e.kind === "work_order" || e.kind === "work_done" ? (
                <div>
                  {e.detail?.id ? (
                    <Link to={`/work-orders/${encodeURIComponent(String(e.detail.id))}`}>{e.title}</Link>
                  ) : (
                    e.title
                  )}
                </div>
              ) : (
                <div className={s.noteBody}>{e.title}</div>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState>{t("Nothing is recorded against this asset yet.")}</EmptyState>
      )}
    </Card>
  );
}

function Notes({ assetId, canWrite }: { assetId: string; canWrite: boolean }) {
  const q = useAmNotes(assetId);
  const add = useAmAddNote(assetId);
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<NoteCategory>("observation");
  return (
    <div className={s.split}>
      <Card title={t("Notes")} sub={t("Notes can't be edited or deleted.")}>
        {q.isLoading && <Skeleton rows={4} />}
        {q.error && <ErrorState error={q.error} retry={() => void q.refetch()} />}
        {q.data &&
          (q.data.length ? (
            <ul className={s.notes} data-testid="am-notes">
              {q.data.map((n) => (
                <li key={n.id} className={s.noteItem}>
                  <div className={s.eventHead}>
                    <b>{t(NOTE_CATEGORY_LABEL[n.category] ?? n.category)}</b>
                    <span className={s.eventWhen}>{formatDateTime(n.createdAt)}</span>
                    {n.createdBy && <span className={s.dim}>{t("by {who}", { who: n.createdBy })}</span>}
                  </div>
                  <div className={s.noteBody}>{n.body}</div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState>{t("No notes on this asset yet.")}</EmptyState>
          ))}
      </Card>
      {canWrite && (
        <Card title={t("Add a note")}>
          <div className={s.form}>
            <Field label="Kind of note">
              <Select value={category} onChange={(e) => setCategory(e.target.value as NoteCategory)}>
                {(Object.keys(NOTE_CATEGORY_LABEL) as NoteCategory[]).map((c) => (
                  <option key={c} value={c}>
                    {t(NOTE_CATEGORY_LABEL[c])}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <textarea
            className={s.textarea}
            style={{ minHeight: 110, marginTop: 10 }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("What did you see, do or recommend?")}
            aria-label={t("Note")}
            maxLength={4000}
            data-testid="am-note-text"
          />
          <div className={s.formFoot}>
            <Button
              variant="primary"
              disabled={!body.trim() || add.isPending}
              onClick={() => add.mutate({ body: body.trim(), category }, { onSuccess: () => setBody("") })}
              data-testid="am-note-add"
            >
              {add.isPending ? t("Saving…") : t("Add the note")}
            </Button>
            {add.error && <span className={s.err}>{errorText(add.error)}</span>}
          </div>
        </Card>
      )}
    </div>
  );
}
